import React, { useState } from 'react';
import styled from 'styled-components';
import { User, UserCog, X } from 'lucide-react';
import StaffPicker from './StaffPicker';

interface ReassignControlProps {
  assignedUserId?: number; // null/undefined ⇒ לא-משויך (למשל תזכורת מאגר-המנהלים)
  assignedUserName?: string;
  /** מבצע את קריאת ה-API לשינוי השיוך. יש לזרוק שגיאה בכשל כדי שהעורך יישאר פתוח לניסיון חוזר. */
  onReassign: (userId: number) => Promise<void>;
  disabled?: boolean;
}

const Wrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const AssigneeText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: var(--text-secondary);
`;

const ChangeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 5px;
  transition: background 0.2s ease;

  &:hover {
    background: var(--primary-light);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 190px;
`;

const CancelButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  border-radius: 5px;

  &:hover {
    color: var(--error-color);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * שליטה קומפקטית בשיוך של פריט מתוזמן: מציגה את שם המשויך + כפתור "שנה משויך".
 * בלחיצה נפתח StaffPicker inline, ובחירת עובד אחר שומרת מיד דרך onReassign.
 */
const ReassignControl: React.FC<ReassignControlProps> = ({
  assignedUserId,
  assignedUserName,
  onReassign,
  disabled,
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = async (userId: number) => {
    if (!userId || userId === assignedUserId) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onReassign(userId);
      setEditing(false);
    } catch {
      // הודעת השגיאה מוצגת ע"י הקורא (toast) — משאירים את העורך פתוח לניסיון חוזר
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <EditRow>
        <StaffPicker label="" value={assignedUserId ?? 0} onChange={handleChange} disabled={saving} />
        <CancelButton
          type="button"
          onClick={() => setEditing(false)}
          disabled={saving}
          aria-label="ביטול"
          title="ביטול"
        >
          <X size={14} />
        </CancelButton>
      </EditRow>
    );
  }

  return (
    <Wrap>
      <AssigneeText>
        <User size={14} strokeWidth={2} />
        {assignedUserName || 'לא משויך'}
      </AssigneeText>
      <ChangeButton type="button" onClick={() => setEditing(true)} disabled={disabled}>
        <UserCog size={13} strokeWidth={2} />
        שנה משויך
      </ChangeButton>
    </Wrap>
  );
};

export default ReassignControl;
